import {
    BatchErrorDTO,
    BatchFileFilterDTO,
    BatchFileStatisticsDTO,
    SyncBatch,
    SyncBatchStatus,
    SyncBatchTransformation,
    SyncBatchTransformationStatus,
    UUIDv1
} from '@beable/roster-backend-client-v1';
import {
    AccordionActions,
    AccordionDetails,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Alert,
    Box
} from '@mui/material';
import {
    Check,
    Delete as DeleteIcon,
    PlayArrow,
    Warning,
    HourglassBottomOutlined,
} from '@mui/icons-material';
import React, { useEffect, useState, MouseEvent } from 'react';
import { useTheme } from '@mui/material/styles';
import batchService from '../../../services/BatchService';
import ChangesModal from '../Modals/ChangesModal';
import ResultsModal from '../Modals/ResultsModal';
import { getStyles } from '../styles';

const dateOptions: Intl.DateTimeFormatOptions = {
    hour12: true,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
};

const dateOptionsShort: Intl.DateTimeFormatOptions = {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
};
const statusIcons = {
    failed: <Warning />,
    succeeded: <Check />,
    running: <HourglassBottomOutlined />,
};
const initialMessageSettings: { open: boolean; message: string; severity: "error" | "success" | "info" | "warning" | undefined } = {
    open: false,
    message: '',
    severity: undefined
};

// the below maps as follows: [academicSessions, orgs, classes, courses, enrollments, batchWarnings, demographics, users]
const initialPageCounts = Array(8).fill(0);

/**  SyncBatchStatus.FAILED, SyncBatchStatus.LOCK_TIMEOUT */
const failedStatuses = [SyncBatchStatus.FAILED, SyncBatchStatus.LOCK_TIMEOUT];

interface BatchFileListProps {
    batch: SyncBatch;
    transformations: SyncBatchTransformation[];
    batchErrorDTOS: BatchErrorDTO[];
    onActionComplete: (siteId: number) => void;
}


export default function BatchFileList({ batch, transformations, batchErrorDTOS, onActionComplete }: BatchFileListProps) {
    const theme = useTheme();
    const styles = getStyles(theme);

    const [batchStatistics, setBatchStatistics] = useState<BatchFileStatisticsDTO[]>([]);
    const [filteredData, setFilteredData] = useState<BatchFileFilterDTO[]>([]);
    const [batchErrors, setBatchErrors] = useState<string[]>([]);
    const [isLoadingAction, setIsLoadingAction] = useState(false);
    const [resultsModalOpened, setResultsModalOpened] = useState(false);
    const [selectedFile, setSelectedFile] = useState<string | null>(null);
    const [filesWithWarnings, setFilesWithWarnings] = useState<string[]>([]);
    const [messageSettings, setMessageSettings] = useState(initialMessageSettings);
    const [areBatchWarnings, setAreBatchWarnings] = useState(false);
    const [changesModalOpened, setChangesModalOpened] = useState(false);
    const [pagesCount, setPagesCount] = useState(0);
    const [pageCounts, setPageCounts] = useState(initialPageCounts);

    useEffect(() => {
        const fetchStatistics = async () => {
            const statistics = await batchService.getBatchStatistics(batch.legacyBatchId);
            if (!statistics) {
                window.alert("No statistics found...");
                return;
            }

            setBatchStatistics(statistics);
            const filteredData = await batchService.getBatchFilteredData(batch.legacyBatchId, 0, "");
            if (!filteredData) {
                return;
            }
            const tempFilesWithWarnings: Set<string> = new Set();

            filteredData.data.forEach((row: BatchFileFilterDTO) => {
                tempFilesWithWarnings.add(row.fileName);
                if (!row.fileName.includes('.csv')) {
                    setAreBatchWarnings(true);
                }
            });

            setPageCounts([
                filteredData.academicSessionsPageCount,
                filteredData.orgsPageCount,
                filteredData.classesPageCount,
                filteredData.coursesPageCount,
                filteredData.enrollmentsPageCount,
                filteredData.batchWarningsPageCount,
                filteredData.demographicsPageCount,
                filteredData.usersPageCount
            ]);

            setFilesWithWarnings(Array.from(tempFilesWithWarnings));
            setFilteredData(filteredData.data);
        };

        fetchStatistics();
    }, [batch.legacyBatchId]);

    useEffect(() => {
        if (batchErrorDTOS) {
            const stringErrors = batchErrorDTOS.map(err => {
                return err?.fileName?.includes('.csv')
                    ? `Error in ${err.fileName}: ${err.errorText}`
                    : `General Failure: ${err?.errorText ?? 'Unknown Error Occurred'}`;
            });
            setBatchErrors(stringErrors);
        }
    }, [batchErrorDTOS]);

    const approveBatch = async (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setIsLoadingAction(true);
        const resumeRes = await batchService.approveBatch(batch.siteId, e.currentTarget.getAttribute('data-id')! as UUIDv1);
        setIsLoadingAction(false);

        if (!resumeRes) {
            showMessage("Could not approve the batch!", "error");
        } else {
            showMessage("Batch approved successfully!", "success");
            onActionComplete(batch.siteId);
        }
    };

    const rejectBatch = async (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setIsLoadingAction(true);
        const rejectRes = await batchService.cancelBatch(e.currentTarget.getAttribute('data-id')! as UUIDv1, batch.siteId);
        setIsLoadingAction(false);
        if (!rejectRes) {
            showMessage("Could not abandon the batch!", "error");
        } else {
            showMessage("Batch abandoned successfully!", "success");
            onActionComplete(batch.siteId);
        }
    };

    const openResultsModal = (e: MouseEvent<HTMLElement>) => {
        const selectedFile = e.currentTarget.id;
        if (selectedFile?.includes('.csv')) {
            setSelectedFile(selectedFile);

            const idx = getIndex(selectedFile);
            setPagesCount(idx !== -1 ? pageCounts[idx] : 0);

        } else {
            setPagesCount(pageCounts[5]);
        }

        setResultsModalOpened(true);
    }
    const getIndex = (name: string) => {
        // the eight numbers above map as follows:  orgsPageCount = 1, classesPageCount = 2, coursesPageCount = 3,
        // enrollmentsPageCount = 4, batchWarningsPageCount = 5, demographicsPageCount = 6, usersPageCount = 7
        if (name.includes('academicSessions')) return 0;
        if (name.includes('orgs')) return 1;
        if (name.includes('classes')) return 2;
        if (name.includes('courses')) return 3;
        if (name.includes('enrollments')) return 4;
        if (name.includes('demographics')) return 6;
        if (name.includes('users')) return 7;
        return -1;
    }
    const closeResultsModal = () => {
        setResultsModalOpened(false);
        setSelectedFile('');
        setPagesCount(0)
    };

    const openGeneralChangesModal = (e: MouseEvent<HTMLElement>) => {
        setSelectedFile(null);
        setChangesModalOpened(true);
    };

    const openChangesModal = (e: MouseEvent<HTMLElement>) => {
        if (e.currentTarget.id?.includes('.csv')) {
            setSelectedFile(e.currentTarget.id);
        }

        setChangesModalOpened(true);
    };

    const closeChangesModal = () => {
        setChangesModalOpened(false);
    };

    const showMessage = (msg: string, severity: "error" | "success" | "info" | "warning" | undefined) => {
        setMessageSettings({ open: true, message: msg, severity });
    };

    const closeMessage = () => {
        setMessageSettings({ open: false, message: '', severity: messageSettings.severity });
    };

    const formatDate = (date: Date, options: Intl.DateTimeFormatOptions): string => {
        return new Date(date).toLocaleDateString('en-US', options);
    };

    const getStatusStyle = (status: SyncBatchTransformationStatus) => {
        return {
            [SyncBatchTransformationStatus.COMPLETED]: styles.successIcon,
            [SyncBatchTransformationStatus.FAILED]: styles.warnIcon,
            [SyncBatchTransformationStatus.RUNNING]: styles.runIcon
        }[status];
    };

    const getStatusIcon = (status: SyncBatchTransformationStatus) => {
        return {
            [SyncBatchTransformationStatus.COMPLETED]: statusIcons.succeeded,
            [SyncBatchTransformationStatus.FAILED]: statusIcons.failed,
            [SyncBatchTransformationStatus.RUNNING]: statusIcons.running
        }[status];
    };

    const getNumberTransfComplete = (batch: SyncBatch) => {
        const completedTransformation = batch.lastCompletedTransformationIndex != null ? batch.lastCompletedTransformationIndex + 1 : 0;
        const totalCount = batch.transformationClassNames.length;

        if (batch.status === SyncBatchStatus.COMPLETED && totalCount !== completedTransformation) {
            return `${completedTransformation}/${completedTransformation}`;
        }

        return `${completedTransformation}/${totalCount}`;
    }

    return <>
        <Divider />
        <AccordionActions>
            <Grid sx={styles.batchSummary}>
                {batch.status === SyncBatchStatus.SUSPENDED &&
                    filteredData &&
                    filteredData.length === 0 &&
                    <Typography variant="body1">Please review the statistics below per file. Approve batch?</Typography>}
                {batch.status === SyncBatchStatus.SUSPENDED &&
                    filteredData &&
                    filteredData.length > 0 &&
                    <div>
                        {areBatchWarnings && <Typography>
                            Data consistency warning.
                            <Button sx={styles.listControls} color="primary" onClick={openResultsModal}>Click here to view the data issues</Button>
                        </Typography>}
                        <Typography>Approve the batch?</Typography>
                    </div>}
                {batch.status === SyncBatchStatus.COMPLETED &&
                    <div>
                        <Button sx={styles.listControls} color="primary" onClick={openGeneralChangesModal}>Batch Changes Report</Button>
                    </div>
                }
                {failedStatuses.includes(batch.status) &&
                    <div>
                        {
                            batchErrors.map((err, idx) => {
                                return <Typography key={`error${idx}`} variant="body1"> {err}</Typography>;
                            })
                        }
                    </div>
                }
                {batch.status === SyncBatchStatus.CANCELED &&
                    <div>
                        <Typography variant="body1">Abandoned on {formatDate(batch.updatedAt, dateOptions)}</Typography>
                    </div>}
                {batch.status === SyncBatchStatus.SUSPENDED && isLoadingAction &&
                    <Box sx={styles.loaderContainer}>
                        <Box sx={styles.placeHolder}>
                            <CircularProgress disableShrink />
                        </Box>
                    </Box>}
                {batch.status === SyncBatchStatus.SUSPENDED && !isLoadingAction ? (
                    <Grid sx={styles.batchControls}>
                        <Button
                            variant="contained"
                            sx={styles.button}
                            data-id={batch.id}
                            startIcon={<PlayArrow />}
                            onClick={approveBatch}>
                            Approve
                        </Button>
                        <Button
                            variant="contained"
                            sx={styles.button}
                            data-id={batch.legacyBatchId}
                            color="secondary"
                            startIcon={<DeleteIcon />}
                            onClick={rejectBatch}
                        >
                            Abandon
                        </Button>
                    </Grid>) : ''}
            </Grid>
        </AccordionActions>
        <Divider />
        <AccordionDetails>
            <Typography sx={styles.batchSummary} variant='h6'>Batch Statistics</Typography>

            <TableContainer component={Paper} sx={styles.tableRoot}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={styles.tableHeader}></TableCell>
                            <TableCell sx={styles.tableHeader} align="left">File</TableCell>
                            <TableCell sx={styles.tableHeader} align="left">Total Records</TableCell>
                            <TableCell sx={styles.tableHeader} align="left">Added Records</TableCell>
                            <TableCell sx={styles.tableHeader} align="left">Removed Records</TableCell>
                            <TableCell sx={styles.tableHeader} align="left">Updated Records</TableCell>
                            <TableCell sx={styles.tableHeader} align="left">Filtered Records</TableCell>
                            <TableCell sx={styles.tableHeader} align="left">Data Logs</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {batchStatistics.map((batchStat, index) => {
                            return (
                                <TableRow key={index}>
                                    <TableCell
                                        align="left"
                                        sx={filesWithWarnings.includes(batchStat.fileName) ? styles.warnIcon : styles.successIcon}>
                                        {filesWithWarnings.includes(batchStat.fileName) ? statusIcons.failed : statusIcons.succeeded}
                                    </TableCell>
                                    <TableCell sx={styles.tableCell} align="left">{batchStat.fileName}</TableCell>
                                    <TableCell sx={styles.tableCell} align="left">{batchStat.totalRecords}</TableCell>
                                    <TableCell sx={styles.tableCell} align="left">{batchStat.addedRecords}</TableCell>
                                    <TableCell sx={styles.tableCell} align="left">{batchStat.removedRecords}</TableCell>
                                    <TableCell sx={styles.tableCell} align="left">{batchStat.updatedRecords}</TableCell>
                                    <TableCell sx={styles.tableCell} align="left">{batchStat.filteredRecords}</TableCell>
                                    <TableCell sx={styles.tableCell} align="left">
                                        {filesWithWarnings.includes(batchStat.fileName) &&
                                            <Button id={batchStat.fileName} color="primary" onClick={openResultsModal}>
                                                Filtered Data
                                            </Button>}
                                        {batch.status === SyncBatchStatus.COMPLETED &&
                                            <Button id={batchStat.fileName} color="primary" onClick={openChangesModal}>
                                                Changes Report
                                            </Button>}
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </AccordionDetails>

        <AccordionDetails>
            <Typography sx={styles.batchSummary} variant='h6'>Batch Transformations</Typography>
            <Typography sx={styles.batchSummary} variant='body2'>{getNumberTransfComplete(batch)} complete</Typography>
            <TableContainer component={Paper} sx={styles.tableRoot}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={styles.tableHeader}></TableCell>
                            <TableCell sx={styles.tableHeader} align="left">Name</TableCell>
                            <TableCell sx={styles.tableHeader} align="left">Started At</TableCell>
                            <TableCell sx={styles.tableHeader} align="left">Finished at</TableCell>
                            <TableCell sx={styles.tableHeader} align="left">Message</TableCell>
                            <TableCell sx={styles.tableHeader} align="left">Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transformations.map((transf, index) => {
                            return (
                                <TableRow key={index}>
                                    <TableCell sx={getStatusStyle(transf.status)} align="left"> {getStatusIcon(transf.status)}</TableCell>
                                    <TableCell sx={styles.tableCell} align="left">{transf.name}</TableCell>
                                    <TableCell sx={styles.tableCell} align="left">{formatDate(transf.startedAt, dateOptionsShort)}</TableCell>
                                    <TableCell sx={styles.tableCell} align="left">{transf.finishedAt ? formatDate(transf.finishedAt, dateOptionsShort): ''}</TableCell>
                                    <TableCell sx={styles.tableCell} align="left">{transf.statusMessage}</TableCell>
                                    <TableCell sx={styles.tableCell} align="left">{transf.status}</TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </AccordionDetails>
        <ResultsModal
            open={resultsModalOpened}
            warnings={filteredData}
            handleClose={closeResultsModal}
            selectedFile={selectedFile}
            batchId={batch.legacyBatchId}
            pages={pagesCount}
        />
        <ChangesModal
            open={changesModalOpened}
            batchId={batch.legacyBatchId}
            selectedFile={selectedFile}
            handleClose={closeChangesModal}
        />
        <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={messageSettings.open}
            autoHideDuration={5000}
            onClose={closeMessage}>
            <Alert onClose={closeMessage} severity={messageSettings.severity}>
                {messageSettings.message}
            </Alert>
        </Snackbar>
    </>;
}
